/**
 * ForgotPasswordPage Component
 * Handles password reset flow with Cognito.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, AlertCircle, KeyRound, ArrowLeft, CheckCircle, Check, X } from 'lucide-react';
import { PageLayout } from '@/components';
import { useAuthContext } from '@/contexts/AuthContext';
import { Button, Input, Card, IconContainer } from '@/components/ui';
import { validatePassword } from '@/lib/password';

type Step = 'request' | 'confirm' | 'success';

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { forgotPassword, confirmForgotPassword, error: authError } = useAuthContext();

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRequestReset = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await forgotPassword(email);
      setStep('confirm');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('forgotPassword.failedSendReset');
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [email, forgotPassword]);

  const passwordValidation = useMemo(() => validatePassword(newPassword), [newPassword]);

  const handleConfirmReset = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError(t('forgotPassword.passwordsDoNotMatch'));
      return;
    }

    if (!passwordValidation.isValid) {
      setError(t('forgotPassword.passwordRequirements'));
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmForgotPassword(email, code, newPassword);
      setStep('success');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('forgotPassword.failedResetPassword');
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [email, code, newPassword, confirmPassword, confirmForgotPassword]);

  const displayError = error || authError;

  // Success state
  if (step === 'success') {
    return (
      <PageLayout>
        <div className="flex items-center justify-center px-4 pt-20 pb-12 sm:pt-24">
          <div className="max-w-md w-full text-center">
            <IconContainer variant="success" size="xl" className="mx-auto mb-4">
              <CheckCircle className="w-8 h-8" />
            </IconContainer>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {t('forgotPassword.resetComplete')}
            </h1>
            <p className="text-slate-600 mb-8">
              {t('forgotPassword.resetCompleteMessage')}
            </p>
            <Button
              onClick={() => navigate('/login')}
              variant="gradient-blue"
              size="lg"
              shape="rounded"
              fullWidth
            >
              {t('forgotPassword.signIn')}
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="flex items-center justify-center px-4 pt-20 pb-12 sm:pt-24">
        <div className="max-w-md w-full">
          {/* Back button */}
          <button
            onClick={() => step === 'request' ? navigate('/login') : setStep('request')}
            className="mb-6 flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 'request' ? t('forgotPassword.backToSignIn') : t('common.back')}
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <IconContainer variant="primary" size="xl" className="mx-auto mb-4">
              <KeyRound className="w-8 h-8" />
            </IconContainer>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {step === 'request' ? t('forgotPassword.resetYourPassword') : t('forgotPassword.enterResetCode')}
            </h1>
            <p className="text-slate-600">
              {step === 'request'
                ? t('forgotPassword.enterEmailForReset')
                : t('forgotPassword.sentCodeTo', { email })}
            </p>
          </div>

          {/* Error Display */}
          {displayError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{displayError}</p>
            </div>
          )}

          {/* Request Reset Form */}
          {step === 'request' && (
            <Card variant="form" padding="xl">
              <form onSubmit={handleRequestReset}>
                <div className="space-y-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      {t('forgotPassword.emailLabel')}
                    </label>
                    <Input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                      leftIcon={<Mail className="w-5 h-5" />}
                      inputSize="lg"
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="gradient-blue"
                  size="lg"
                  shape="rounded"
                  fullWidth
                  className="mt-6"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('forgotPassword.sending')}
                    </span>
                  ) : (
                    t('forgotPassword.sendResetCode')
                  )}
                </Button>
              </form>
            </Card>
          )}

          {/* Confirm Reset Form */}
          {step === 'confirm' && (
            <Card variant="form" padding="xl">
              <form onSubmit={handleConfirmReset}>
                <div className="space-y-5">
                  {/* Verification Code */}
                  <div>
                    <label htmlFor="code" className="block text-sm font-medium text-slate-700 mb-2">
                      {t('forgotPassword.verificationCode')}
                    </label>
                    <Input
                      type="text"
                      id="code"
                      value={code}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
                      inputSize="lg"
                      className="text-center text-lg tracking-widest"
                      placeholder="123456"
                      required
                      autoComplete="one-time-code"
                      maxLength={6}
                    />
                  </div>

                  {/* New Password */}
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700 mb-2">
                      {t('forgotPassword.newPassword')}
                    </label>
                    <Input
                      type="password"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                      leftIcon={<Lock className="w-5 h-5" />}
                      inputSize="lg"
                      placeholder={t('forgotPassword.newPasswordPlaceholder')}
                      required
                      autoComplete="new-password"
                    />
                    {newPassword && (
                      <ul className="mt-2 space-y-1">
                        {passwordValidation.checks.map((check) => (
                          <li key={check.label} className="flex items-center gap-1.5 text-xs">
                            {check.met ? (
                              <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                            ) : (
                              <X className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            )}
                            <span className={check.met ? 'text-emerald-600' : 'text-slate-500'}>
                              {check.label}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-2">
                      {t('forgotPassword.confirmPassword')}
                    </label>
                    <Input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                      leftIcon={<Lock className="w-5 h-5" />}
                      inputSize="lg"
                      placeholder={t('forgotPassword.confirmPasswordPlaceholder')}
                      required
                      autoComplete="new-password"
                      minLength={8}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="gradient-blue"
                  size="lg"
                  shape="rounded"
                  fullWidth
                  className="mt-6"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('forgotPassword.resetting')}
                    </span>
                  ) : (
                    t('forgotPassword.resetPassword')
                  )}
                </Button>

                {/* Resend code link */}
                <p className="mt-4 text-center text-sm text-slate-600">
                  {t('forgotPassword.didntReceiveCode')}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      handleRequestReset({ preventDefault: () => {} } as React.FormEvent);
                    }}
                    className="text-blue-600 font-medium hover:text-blue-700 transition-colors"
                  >
                    {t('forgotPassword.resend')}
                  </button>
                </p>
              </form>
            </Card>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default ForgotPasswordPage;
